const DEFAULT_MAX_OUTPUT_BYTES = 5 * 1024 * 1024
const DEFAULT_MAX_DIMENSION = 4096
const WEBP_QUALITY_STEPS = [0.9, 0.82, 0.74, 0.66, 0.58, 0.5]
const IMAGE_FILE_EXTENSION = /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|tiff?|webp)$/i

type DecodedImage = {
    source: CanvasImageSource
    width: number
    height: number
    release: () => void
}

type ConvertImageToWebPOptions = {
    maxOutputBytes?: number
    maxDimension?: number
}

export function isImageUploadFile(file: File): boolean {
    return file.type.startsWith("image/") || IMAGE_FILE_EXTENSION.test(file.name)
}

function getWebPFileName(fileName: string): string {
    const baseName = fileName.replace(/\.[^.]+$/, "").trim() || "image"
    return `${baseName}.webp`
}

async function decodeImage(file: File): Promise<DecodedImage> {
    if (typeof createImageBitmap === "function") {
        try {
            const bitmap = await createImageBitmap(file, {
                imageOrientation: "from-image",
            })

            return {
                source: bitmap,
                width: bitmap.width,
                height: bitmap.height,
                release: () => bitmap.close(),
            }
        } catch {
            // Fall back to an HTML image for formats supported by the browser renderer.
        }
    }

    const objectUrl = URL.createObjectURL(file)
    const image = new Image()
    image.decoding = "async"

    try {
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve()
            image.onerror = () => reject(new Error(`Unable to decode ${file.name}.`))
            image.src = objectUrl
        })

        return {
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            release: () => URL.revokeObjectURL(objectUrl),
        }
    } catch (error) {
        URL.revokeObjectURL(objectUrl)
        throw error
    }
}

function canvasToWebPBlob(
    canvas: HTMLCanvasElement,
    quality: number,
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob || blob.type !== "image/webp") {
                    reject(
                        new Error(
                            "This browser cannot convert images to WebP format.",
                        ),
                    )
                    return
                }

                resolve(blob)
            },
            "image/webp",
            quality,
        )
    })
}

function fitWithinDimension(
    width: number,
    height: number,
    maxDimension: number,
): { width: number; height: number } {
    const longestSide = Math.max(width, height)
    if (longestSide <= maxDimension) {
        return { width, height }
    }

    const ratio = maxDimension / longestSide
    return {
        width: Math.max(1, Math.round(width * ratio)),
        height: Math.max(1, Math.round(height * ratio)),
    }
}

export async function convertImageToWebP(
    file: File,
    options: ConvertImageToWebPOptions = {},
): Promise<File> {
    if (!isImageUploadFile(file)) {
        return file
    }

    const maxOutputBytes = options.maxOutputBytes ?? DEFAULT_MAX_OUTPUT_BYTES
    const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION
    const decoded = await decodeImage(file)

    if (decoded.width < 1 || decoded.height < 1) {
        decoded.release()
        throw new Error(`${file.name} does not contain a valid image.`)
    }

    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d", { alpha: true })
    if (!context) {
        decoded.release()
        throw new Error("Image conversion is not available in this browser.")
    }

    let targetSize = fitWithinDimension(
        decoded.width,
        decoded.height,
        maxDimension,
    )
    let smallestBlob: Blob | null = null

    try {
        for (let resizeAttempt = 0; resizeAttempt < 7; resizeAttempt += 1) {
            canvas.width = targetSize.width
            canvas.height = targetSize.height
            context.clearRect(0, 0, canvas.width, canvas.height)
            context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height)

            for (const quality of WEBP_QUALITY_STEPS) {
                const blob = await canvasToWebPBlob(canvas, quality)
                if (!smallestBlob || blob.size < smallestBlob.size) {
                    smallestBlob = blob
                }

                if (blob.size <= maxOutputBytes) {
                    return new File([blob], getWebPFileName(file.name), {
                        type: "image/webp",
                        lastModified: file.lastModified,
                    })
                }
            }

            targetSize = {
                width: Math.max(1, Math.round(targetSize.width * 0.82)),
                height: Math.max(1, Math.round(targetSize.height * 0.82)),
            }
        }
    } finally {
        decoded.release()
        canvas.width = 1
        canvas.height = 1
    }

    if (smallestBlob && smallestBlob.size <= maxOutputBytes) {
        return new File([smallestBlob], getWebPFileName(file.name), {
            type: "image/webp",
            lastModified: file.lastModified,
        })
    }

    throw new Error(
        `${file.name} could not be converted below ${Math.round(maxOutputBytes / 1024 / 1024)} MB.`,
    )
}
